import { Link } from 'react-router-dom';
import { useSiteContent } from '@/context/SiteContentContext';
import { useTheme } from '@/context/ThemeContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

export default function HeroSection() {
  const { content } = useSiteContent();
  const { hero } = content;
  const { theme } = useTheme();
  const { settings } = useBadgeVisibility();
  const light = theme === 'light';

  const bgImage = light ? hero.imageLight : hero.imageDark;

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          key={bgImage}
          src={bgImage}
          alt="Marine background"
          className="w-full h-full object-cover object-top transition-opacity duration-700"
        />
        {light ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/50 to-white/10"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-white/20"></div>
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2A]/90 via-[#0D1B2A]/70 to-[#0D1B2A]/40"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/80 via-transparent to-[#0D1B2A]/30"></div>
          </>
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-10 pt-24 pb-16">
        <div className="max-w-2xl">
          {/* Badge */}
          {settings.heroBadge && (
            <div
              className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', color: 'var(--accent)' }}
            >
              <i className="ri-global-line"></i>
              {hero.badge}
            </div>
          )}

          {/* Headline */}
          <h1 className={`text-5xl md:text-6xl lg:text-7xl font-black leading-tight mb-6 tracking-tight ${light ? 'text-gray-900' : 'text-white'}`}>
            {hero.headline1}<br />
            <span style={{ color: 'var(--accent)' }}>{hero.headline2}</span>
          </h1>

          {/* Subtext */}
          <p className={`text-lg md:text-xl leading-relaxed mb-10 max-w-xl ${light ? 'text-gray-600' : 'text-white/70'}`}>
            {hero.subtext}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/products"
              className="text-white font-bold px-8 py-4 rounded-md transition-colors text-center cursor-pointer whitespace-nowrap text-sm tracking-wide uppercase"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              {hero.cta1}
            </Link>
            <Link
              to="/tech-wiki"
              className={`border font-bold px-8 py-4 rounded-md transition-colors text-center cursor-pointer whitespace-nowrap text-sm tracking-wide uppercase ${
                light
                  ? 'border-gray-400 text-gray-800 hover:bg-gray-100'
                  : 'border-white/40 text-white hover:bg-white/10'
              }`}
            >
              {hero.cta2}
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce ${light ? 'text-gray-400' : 'text-white/40'}`}>
        <i className="ri-arrow-down-line text-xl"></i>
      </div>
    </section>
  );
}
